/**
 * ScrollToTop — forces window scroll to (0,0) on every pathname change.
 *
 * React Router doesn't reset scroll position on navigation by default, so
 * tapping a Dashboard card could open its destination page already
 * scrolled halfway down (wherever the Dashboard was scrolled to when the
 * user tapped). This component fixes that globally.
 *
 * Owen Jul 2026 — also handles URL hash anchors. When navigating to a
 * path like `/premium#medallion`, plain react-router-dom v6 ignores the
 * hash entirely (it scrolls to 0 and stops). We now wait one frame for
 * the target page to mount, then scroll the anchor element into view.
 *
 * Mount once inside <BrowserRouter> in App.js. Renders nothing.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Small delay so the target page has time to render before we
      // ask the browser to locate its anchor. `requestAnimationFrame`
      // twice is a well-known trick that lands *after* the first paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const id = hash.replace('#', '');
          const el = id && document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
          }
          window.scrollTo(0, 0);
        });
      });
      return;
    }
    // Instant, not smooth — a smooth scroll on every route change feels laggy.
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

export default ScrollToTop;
